import React, { FC, useEffect } from 'react';
import { SEO } from 'components/seo/SEO';
import { breadcrumbJsonLd } from 'components/seo/organizationSchema';
import routeManifest from 'routes/routeManifest.json';
import './TermsOfUse.css';

const route = routeManifest.find((r) => r.path === '/terms-of-use')!;
const breadcrumbs = breadcrumbJsonLd([
  { name: 'Home', path: '/' },
  { name: 'Terms of Use', path: '/terms-of-use' },
]);

export const TermsOfUse: FC = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="terms-page">
      <SEO title={route.title} description={route.description} path={route.path} jsonLd={[breadcrumbs]} />
      <div className="terms-container">
        <h1>Toastie Terms of Use</h1>
        <p className="effective-date">Effective Date: 12 Apr 2026</p>

        <section>
          <p>Welcome to Toastie. These Terms of Use (“Terms”) govern your access to and use of the Toastie mobile application and related services (the “Services”), operated by Toastie (“we”, “us”, or “our”). By accessing or using Toastie, you agree to these Terms. If you do not agree, you must not use the Services.</p>
        </section>

        <section>
          <h2>1. Overview of Toastie</h2>
          <p>
            Toastie is a personal health tracking platform designed to help users log symptoms, track patterns, and generate insights about their health. Toastie includes features powered by artificial intelligence (“AI”) to assist with identifying patterns, summarising user data, and providing general informational insights.
          </p>
          <p>Toastie is not a medical service and does not replace professional healthcare providers.</p>
        </section>

        <section className="warning-section">
          <h2>2. Important: No Medical Advice</h2>
          <p>Toastie does not provide medical advice. The information, insights, and AI-generated outputs provided by Toastie are for general informational purposes only. In particular:</p>
          <ul>
            <li>Toastie does not diagnose, treat, or prevent any condition.</li>
            <li>AI-generated summaries, pattern insights, triage suggestions, and lab report callouts are informational only and are not a substitute for professional medical assessment.</li>
            <li>Use of Toastie does not create a doctor-patient relationship.</li>
            <li>You must always seek advice from a qualified healthcare professional regarding any medical concerns.</li>
            <li>Do not ignore or delay seeking medical advice based on anything you read in Toastie.</li>
            <li>Your use of Toastie and reliance on any information provided is entirely at your own risk.</li>
          </ul>
        </section>

        <section>
          <h2>3. How Toastie Uses Artificial Intelligence</h2>
          <p>Toastie uses AI in a number of ways to help you understand and track your health. Each AI-powered feature is described below.</p>
          
          <div className="subsection">
            <h3>3a. Meal Photo Analysis</h3>
            <p>You may upload photos of meals or food. Toastie uses AI image recognition to identify potential ingredients from these images. This feature is intended to assist with food logging and is not a nutritional assessment tool. Identified ingredients may be inaccurate and should be reviewed and corrected by you.</p>
          </div>

          <div className="subsection">
            <h3>3b. Speech-to-Text and Prose Categorisation</h3>
            <p>Toastie allows you to speak or type health-related notes in free form. AI is used to transcribe spoken input and to automatically categorise entries into health tracking categories including (but not limited to): symptoms, medication, stool, menstrual cycle, and meals. Categorisation is automated and may not always be accurate — you are encouraged to review and edit entries.</p>
          </div>

          <div className="subsection">
            <h3>3c. Health Questions and Triage Guidance</h3>
            <p>Toastie allows you to ask questions about your health. AI may provide general guidance on whether a symptom warrants a visit to a doctor, monitoring at home, or urgent attention. This is general informational guidance only. It is not a medical triage service and must not replace professional medical judgement in an emergency or for serious health concerns.</p>
            <p><strong>If you believe you are experiencing a medical emergency, call emergency services immediately.</strong></p>
          </div>

          <div className="subsection">
            <h3>3d. Health Data Pattern Analysis</h3>
            <p>Toastie may send your tracked health data (including symptoms, medications, food logs, and other entries) to AI systems to provide contextual answers to your questions and to surface patterns in your data over time. This data is de-identified to the best of our ability before being processed. You acknowledge that no method of de-identification is perfect.</p>
          </div>

          <div className="subsection">
            <h3>3e. Lab Report and Medical Document Summarisation</h3>
            <p>You may upload lab reports (such as blood test results) and other medical documents. Toastie will process these using AI to generate plain-language summaries and highlight key findings. You acknowledge that:</p>
            <ul>
              <li>AI-generated summaries may miss, misinterpret, or incorrectly emphasise information in your documents.</li>
              <li>Summaries are not a clinical interpretation and do not replace a healthcare professional reviewing your results.</li>
              <li>You should only upload documents you are comfortable having processed by automated AI systems.</li>
              <li>Uploaded documents are de-identified to the best of our ability before being sent for AI processing.</li>
            </ul>
          </div>
        </section>

        <section>
          <h2>4. AI Limitations and Acknowledgements</h2>
          <p>By using any AI-powered feature in Toastie, you acknowledge and agree that:</p>
          <ul>
            <li>AI outputs may be incomplete, inaccurate, outdated, or misleading.</li>
            <li>AI systems may produce errors, hallucinations, bias, or incorrect interpretations.</li>
            <li>AI outputs are not medical advice and must not be relied upon as such.</li>
            <li>You are responsible for independently verifying any information before acting on it.</li>
            <li>Third-party AI providers process your data on our behalf. These providers are subject to restrictions on using your data for model training where applicable.</li>
          </ul>
        </section>

        <section>
          <h2>5. User Accounts</h2>
          <p>To use certain features, you may be required to create an account. You agree to provide accurate and up-to-date information, keep your login credentials secure, and be responsible for all activity under your account. You must not impersonate another person, use the app for unlawful purposes, or interfere with the operation of the Services.</p>
        </section>

        <section>
          <h2>6. Your Content</h2>
          <p>You may upload or input information, including health-related information such as symptoms, notes, photos, voice recordings, and medical documents including lab reports (“User Content”). You acknowledge that such information may constitute sensitive health information.</p>
          <p>By using the Services, you:</p>
          <ul>
            <li>Consent to the collection, storage, and processing of your User Content for the purpose of providing and improving the Services.</li>
            <li>Acknowledge that your User Content may be processed using automated systems, including AI, to generate insights, summaries, and categorisations.</li>
            <li>Agree that Toastie may process User Content using third-party service providers (including AI providers) to provide the Services.</li>
            <li>Acknowledge that data submitted to AI systems is de-identified to the best of our ability, but that no de-identification method is perfect.</li>
          </ul>
          <p>You retain ownership of your User Content, but grant Toastie a licence to use, store, process, and transform such content solely for the purpose of operating, maintaining, and improving the Services. You should only upload content you are comfortable being processed in this way.</p>
        </section>

        <section>
          <h2>7. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Misuse or exploit the platform.</li>
            <li>Reverse engineer or copy the app.</li>
            <li>Attempt to access other users’ data.</li>
            <li>Use the app for harmful, illegal, or abusive purposes.</li>
            <li>Upload content belonging to third parties without appropriate rights.</li>
          </ul>
          <p>We may suspend or terminate your access if you violate these Terms.</p>
        </section>

        <section>
          <h2>8. Availability</h2>
          <p>We aim to provide a reliable service, but Toastie may be unavailable at times, features may change or be removed, and we do not guarantee uninterrupted access.</p>
        </section>

        <section>
          <h2>9. Third-Party Services</h2>
          <p>Toastie integrates with third-party service providers to deliver AI functionality, including large language model providers, image recognition services, and speech-to-text processors. These providers process User Content on our behalf. We take reasonable steps to ensure such providers handle data securely and in accordance with applicable privacy standards, including restrictions on using data for AI model training where applicable. Third-party services are governed by their own terms and policies, and we are not responsible for their independent actions.</p>
        </section>

        <section>
          <h2>10. Limitation of Liability</h2>
          <p>To the maximum extent permitted by law, Toastie is provided “as is”. We are not liable for any loss, damage, or harm resulting from: use of the app; reliance on insights, triage guidance, summaries, or AI outputs; errors or inaccuracies in the service; or decisions made based on AI-generated content.</p>
        </section>

        <section>
          <h2>11. Termination</h2>
          <p>We may suspend or terminate your account if you breach these Terms or if we reasonably believe your use may cause harm. You may stop using Toastie at any time.</p>
        </section>

        <section>
          <h2>12. Changes to These Terms</h2>
          <p>We may update these Terms from time to time. Continued use of Toastie after changes are posted means you accept the updated Terms. We will take reasonable steps to notify you of material changes.</p>
        </section>

        <section>
          <h2>13. Privacy</h2>
          <p>Your use of Toastie is also governed by our Privacy Policy. By using Toastie, you acknowledge that we may collect and handle sensitive health information in accordance with applicable privacy laws, including the Australian Privacy Act 1988 (Cth) and the Australian Privacy Principles. We are committed to transparency, data minimisation, and reasonable security safeguards.</p>
        </section>

        <section>
          <h2>14. Governing Law</h2>
          <p>These Terms are governed by the laws of New South Wales, Australia.</p>
        </section>

        <section>
          <h2>15. Contact</h2>
          <p>
            If you have questions about these Terms, contact:<br />
            <strong>Email:</strong> <a href="mailto:hello@toastie.au">hello@toastie.au</a><br />
            <strong>Company:</strong> Toastie
          </p>
        </section>
      </div>
    </div>
  );
};
